import { useEffect, useRef, useState } from 'react';
import { HomeCalendar } from './HomeCalendar.tsx';
import { RewardDrawing } from './RewardDrawing.tsx';
import type { RewardKind } from '../home.ts';
import {
  dailyChecklist,
  selectedSubjects,
  subjectComplete,
  completionReward,
  collectedStamp,
} from '../home.ts';
import { forDate } from '../content/calendar.ts';
import { mapIndex } from '../content/map-index.ts';
import { subjects, subjectLabels } from '../types.ts';
import type { Progress, Subject } from '../types.ts';

const descriptions: Record<Subject, string> = {
  reason: 'One good puzzle',
  math: 'One math problem',
  reading: 'An article + 5 questions',
  foundations: '10 questions to revisit',
  maps: 'Study, then fill the map',
};

function SubjectIcon({ subject }: { subject: Subject }) {
  const paths: Record<Subject, string> = {
    reason: 'M3 8h5V6a3 3 0 0 1 6 0v2h7v5h-2a3 3 0 0 0 0 6h2v4H3Z',
    math: 'M4 5h6M7 2v6M15 5h6M4 15l6 6M10 15l-6 6M15 16h6M15 20h6',
    reading: 'M12 5C8 2 4 3 2 4v15c3-2 7-1 10 1 3-2 7-3 10-1V4c-2-1-6-2-10 1Zm0 0v15',
    foundations: 'M3 8l9-5 9 5-9 5Zm0 5 9 5 9-5M3 18l9 5 9-5',
    maps: 'M2 5l7-3 6 3 7-3v17l-7 3-6-3-7 3Zm7-3v17m6-14v17',
  };
  return (
    <svg viewBox="0 0 24 26" aria-hidden="true" className="subject-icon">
      <path
        d={paths[subject]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Preview({ date, subject }: { date: string; subject: Subject }) {
  if (subject === 'maps') {
    const map = mapIndex[date];
    return (
      <span className="window-preview map-window">
        <strong>{map.title}</strong>
        <img src={map.preview} alt="" loading="lazy" width="280" height="126" />
        <span className="preview-detail">
          {map.placeIds.length} {map.unit}
        </span>
      </span>
    );
  }
  const set = forDate(date, subject)!;
  const excerpt = subject === 'reading' ? set.article!.preview : set.questions[0].prompt;
  return (
    <span className="window-preview">
      <strong>{set.title}</strong>
      <span className="preview-excerpt">{excerpt}</span>
      <span className="preview-detail">
        {subject === 'reading'
          ? 'Read & recall'
          : `${set.questions.length} ${set.questions.length === 1 ? 'question' : 'questions'}`}
      </span>
    </span>
  );
}

function Stamp({ date, kind }: { date: string; kind: RewardKind }) {
  const printedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${date}T12:00:00`));
  return (
    <div
      className={`daily-stamp stamp-${kind}`}
      role="img"
      aria-label={`${kind === 'sun' ? 'Sun · all modules complete' : 'Moon · selected modules complete'}, ${printedDate}`}
    >
      <span className="stamp-top">MENTAT DAILY</span>
      <svg viewBox="0 0 120 82" aria-hidden="true">
        <RewardDrawing kind={kind} />
      </svg>
      <strong>{kind === 'sun' ? 'ALL FIVE COMPLETE' : 'SELECTION COMPLETE'}</strong>
      <span className="stamp-date">{printedDate}</span>
    </div>
  );
}

export function Home({
  date,
  today,
  progress,
  mapDone,
  mapCompletions,
  onDate,
  onConfirm,
  onStamp,
  onOpen,
}: {
  date: string;
  today: string;
  progress: Progress;
  mapDone: boolean;
  mapCompletions: Record<string, boolean>;
  onDate: (date: string) => void;
  onConfirm: (subjects: Subject[]) => void;
  onStamp: () => void;
  onOpen: (subject: Subject) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Subject[]>(() => [...(progress.dailySubjects?.[date] ?? [])]);
  const confirmed = !!progress.dailyConfirmed?.[date];
  const choosing = !confirmed || editing;
  const heading = useRef<HTMLHeadingElement>(null);
  const previousChoosing = useRef(choosing);
  useEffect(() => {
    if (previousChoosing.current !== choosing) {
      heading.current?.focus({ preventScroll: true });
      window.scrollTo(0, 0);
      previousChoosing.current = choosing;
    }
  }, [choosing]);
  const { selected, completed, done } = dailyChecklist(progress, date, mapDone);
  const stamp = collectedStamp(progress, date, mapDone);
  const stamped = !!stamp;
  const reward = completionReward(progress, date, mapDone);
  useEffect(() => {
    // Upgrade an already collected moon when the remaining modules are finished.
    if (stamped && reward && progress.dailyStamps?.[date] !== stamp) onStamp();
  }, [stamped, reward, progress.dailyStamps, date, stamp, onStamp]);
  const [justStamped, setJustStamped] = useState(false);
  function edit() {
    setDraft([...selectedSubjects(progress, date)]);
    setEditing(true);
  }
  function confirm() {
    if (!draft.length) return;
    onConfirm(draft);
    setEditing(false);
  }
  return (
    <section
      className={`daily-home ${choosing ? 'is-choosing' : 'is-planned'}`}
      aria-labelledby="home-title"
    >
      <HomeCalendar
        date={date}
        today={today}
        progress={progress}
        mapCompletions={mapCompletions}
        onDate={onDate}
      />
      <div className="home-heading">
        <h1 id="home-title" ref={heading} tabIndex={-1}>
          {choosing
            ? `Choose what you’re in the mood for${date === today ? ' today' : ''}.`
            : date === today
              ? 'Today’s selection'
              : 'Your selection'}
        </h1>
        {!choosing && (
          <span className="home-count">
            {completed} / {selected.length} complete
          </span>
        )}
      </div>
      {choosing ? (
        <div className="plan-chooser">
          <fieldset className="subject-choices">
            <legend className="sr-only">Choose subjects for this date</legend>
            {subjects.map((subject) => (
              <label
                className={`subject-choice ${draft.includes(subject) ? 'is-chosen' : ''}`}
                key={subject}
              >
                <input
                  type="checkbox"
                  aria-label={`Include ${subjectLabels[subject]}`}
                  checked={draft.includes(subject)}
                  onChange={() =>
                    setDraft((previous) =>
                      previous.includes(subject)
                        ? previous.filter((item) => item !== subject)
                        : [...previous, subject],
                    )
                  }
                />
                <SubjectIcon subject={subject} />
                <span className="choice-name">{subjectLabels[subject]}</span>
                <span className="choice-description">{descriptions[subject]}</span>
              </label>
            ))}
          </fieldset>
          <div className="plan-actions">
            <button className="lock-plan" disabled={!draft.length} onClick={confirm}>
              Lock it in <span aria-hidden="true">→</span>
            </button>
            <span className="selection-count" role="status">
              {draft.length ? `${draft.length} selected` : 'Pick at least one'}
            </span>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`daily-windows windows-${selected.length}`}
            aria-label="Your selected subjects"
          >
            {selected.map((subject) => {
              const complete = subjectComplete(progress, date, subject, mapDone);
              return (
                <button
                  className={`subject-window ${complete ? 'window-complete' : ''}`}
                  key={subject}
                  onClick={() => onOpen(subject)}
                  aria-label={`Open ${subjectLabels[subject]}${complete ? ', complete' : ''}`}
                >
                  <span className="window-bar">
                    <SubjectIcon subject={subject} />
                    <span>{subjectLabels[subject]}</span>
                    <span className="window-indicator" aria-hidden="true">
                      {complete ? '✓' : '↗'}
                    </span>
                  </span>
                  <Preview date={date} subject={subject} />
                  <span className="window-bottom">
                    {complete ? '✓ Complete' : 'Open'}{' '}
                    <span aria-hidden="true">{complete ? '↗' : '→'}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="change-plan">
            <button className="text-button" onClick={edit}>
              Select different options
            </button>
          </div>
          <div className={`home-reward ${justStamped ? 'stamp-landed' : ''}`}>
            <div role="status" aria-live="polite" aria-atomic="true">
              {done && (
                <>
                  <h2>{date === today ? 'Done for the day.' : 'This day is complete.'}</h2>
                  <p>{stamped ? 'Your day, stamped.' : 'One small souvenir for your efforts.'}</p>
                </>
              )}
              {!done && stamped && <p>You’ve already earned this day’s stamp.</p>}
            </div>
            {stamped ? (
              <Stamp date={date} kind={stamp!} />
            ) : (
              done && (
                <button
                  className="stamp-action"
                  onClick={() => {
                    onStamp();
                    setJustStamped(true);
                  }}
                >
                  Stamp my day <span aria-hidden="true">✳</span>
                </button>
              )
            )}
            {done && selected.length < subjects.length && (
              <p className="reward-more">
                Still in the mood?{' '}
                <button className="text-button" onClick={edit}>
                  Add another subject
                </button>
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
